// Enhanced SubmissionComparisonModal with smart analysis and detailed comparison
import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, Calendar, Maximize2, Minimize2 } from '../../common/Icons';

// Enhanced components
import SmartAnalysisCard from './components/SmartAnalysisCard';
import PlayerStatsComparisonGrid from './components/PlayerStatsComparisonGrid';
import LineupValidationPanel from './components/LineupValidationPanel';
import EnhancedActionPanel from './components/EnhancedActionPanel';

// Utilities
import { ComparisonEngine } from '../../../utils/comparisonEngine';

const SubmissionComparisonModal = ({ 
  submission, 
  onApprove, 
  onModify, // NEW: For modification functionality
  onClose,
  isLoading = false,
  teamRosters = {} // NEW: For lineup validation
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState('overview'); // 'overview', 'stats', 'lineup', 'evidence'

  // Extract submission data
  const {
    tournament_name,
    team1_name,
    team2_name,
    team1_submission_data,
    team2_submission_data,
    team1_submitted_by,
    team2_submitted_by,
    team1_submitted_at,
    team2_submitted_at
  } = submission;

  // Perform comprehensive analysis using our comparison engine
  const analysis = useMemo(() => {
    if (!team1_submission_data && !team2_submission_data) return null;
    
    const engine = new ComparisonEngine(
      team1_submission_data,
      team2_submission_data,
      teamRosters
    );
    
    return engine.analyzeSubmissions();
  }, [team1_submission_data, team2_submission_data, teamRosters]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Format date time
  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  // Tab navigation
  const tabs = [
    { id: 'overview', label: 'Overview', icon: Calendar },
    { id: 'stats', label: 'Player Stats', icon: 'user-check' },
    { id: 'lineup', label: 'Lineup', icon: 'users' },
    { id: 'evidence', label: 'Evidence', icon: 'camera' }
  ];

  const TabButton = ({ tab, isActive, onClick }) => (
    <button
      onClick={onClick}
      className={`flex-1 md:flex-none px-3 py-1.5 text-xs font-medium rounded-lg transition-colors whitespace-nowrap ${
        isActive
          ? 'bg-[#31C590]/20 text-[#23F8B5]'
          : 'text-gray-400 hover:text-white hover:bg-white/10'
      }`}
    >
      {tab.label}
    </button>
  );

  const OverviewTab = () => (
    <div className="space-y-4">
      {/* Smart Analysis Card */}
      <SmartAnalysisCard analysis={analysis} />

      {/* Quick Submission Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Team 1 Submission */}
        {team1_submission_data && (
          <div className="p-3 border border-white/[0.06] rounded-xl bg-[#22262f]">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-bold text-base text-white">
                {team1_name} Submission
              </h4>
              <div className="text-xs text-gray-400">
                {formatDateTime(team1_submitted_at)}
              </div>
            </div>

            {/* Score Display */}
            <div className="mb-3 p-2.5 bg-[#191c23] rounded-lg">
              <div className="text-center">
                <div className="text-xl font-bold text-white mb-1">
                  {team1_submission_data.team1_score || 0} - {team1_submission_data.team2_score || 0}
                </div>
                <div className="text-sm text-gray-400">
                  Final Score
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-3 text-center text-sm">
              <div>
                <div className="font-bold text-white">
                  {team1_submission_data.playerStats?.length || team1_submission_data.player_stats?.length || 0}
                </div>
                <div className="text-gray-400">Players</div>
              </div>
              <div>
                <div className="font-bold text-white">
                  {team1_submission_data.mvpPlayerId || team1_submission_data.mvp_player_id ? '✓' : '–'}
                </div>
                <div className="text-gray-400">MVP</div>
              </div>
              <div>
                <div className="font-bold text-white">
                  {(team1_submission_data.team1_screenshot_url || team1_submission_data.team2_screenshot_url) ? '✓' : '–'}
                </div>
                <div className="text-gray-400">Evidence</div>
              </div>
            </div>

            {/* Submitter */}
            <div className="mt-3 pt-3 border-t border-white/[0.06]">
              <p className="text-xs text-gray-400">
                Submitted by <span className="font-medium">{team1_submitted_by}</span>
              </p>
            </div>
          </div>
        )}

        {/* Team 2 Submission */}
        {team2_submission_data && (
          <div className="p-3 border border-white/[0.06] rounded-xl bg-[#22262f]">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-bold text-base text-white">
                {team2_name} Submission
              </h4>
              <div className="text-xs text-gray-400">
                {formatDateTime(team2_submitted_at)}
              </div>
            </div>

            {/* Score Display */}
            <div className="mb-3 p-2.5 bg-[#191c23] rounded-lg">
              <div className="text-center">
                <div className="text-xl font-bold text-white mb-1">
                  {team2_submission_data.team1_score || 0} - {team2_submission_data.team2_score || 0}
                </div>
                <div className="text-sm text-gray-400">
                  Final Score
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-3 text-center text-sm">
              <div>
                <div className="font-bold text-white">
                  {team2_submission_data.playerStats?.length || team2_submission_data.player_stats?.length || 0}
                </div>
                <div className="text-gray-400">Players</div>
              </div>
              <div>
                <div className="font-bold text-white">
                  {team2_submission_data.mvpPlayerId || team2_submission_data.mvp_player_id ? '✓' : '–'}
                </div>
                <div className="text-gray-400">MVP</div>
              </div>
              <div>
                <div className="font-bold text-white">
                  {(team2_submission_data.team1_screenshot_url || team2_submission_data.team2_screenshot_url) ? '✓' : '–'}
                </div>
                <div className="text-gray-400">Evidence</div>
              </div>
            </div>

            {/* Submitter */}
            <div className="mt-3 pt-3 border-t border-white/[0.06]">
              <p className="text-xs text-gray-400">
                Submitted by <span className="font-medium">{team2_submitted_by}</span>
              </p>
            </div>
          </div>
        )}

        {/* Partial Submission Message */}
        {(!team1_submission_data || !team2_submission_data) && (
          <div className="lg:col-span-2 flex items-start gap-3 p-3 bg-amber-500/10 rounded-xl border border-amber-500/30">
            <Calendar className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-white">Single Team Submission</h3>
              <p className="text-xs text-gray-300 mt-0.5">
                {team1_submission_data ? team1_name : team2_name} has submitted their match results for review.
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                You can approve this submission now or wait for {!team1_submission_data ? team1_name : team2_name} to also submit.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const StatsTab = () => (
    <PlayerStatsComparisonGrid 
      playerStatsComparison={analysis?.playerStats}
      team1Name={team1_name}
      team2Name={team2_name}
    />
  );

  const LineupTab = () => (
    <LineupValidationPanel 
      lineupComparison={analysis?.lineups}
      team1Name={team1_name}
      team2Name={team2_name}
    />
  );

  const EvidenceTab = () => (
    <div className="space-y-4">
      <h3 className="text-base font-bold text-white">Evidence Comparison</h3>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 max-h-96 overflow-y-auto">
        {/* Team 1 Evidence */}
        <div className="space-y-3">
          <h4 className="font-medium text-white">{team1_name} Evidence</h4>
          <div className="space-y-2">
            {team1_submission_data?.team1_screenshot_url && (
              <div>
                <p className="text-sm text-gray-400 mb-2">Team 1 Screenshot:</p>
                <img
                  src={team1_submission_data.team1_screenshot_url}
                  alt="Team 1 evidence"
                  className="w-full h-40 object-cover rounded-lg border border-white/10 cursor-pointer hover:scale-105 transition-transform"
                  onClick={() => window.open(team1_submission_data.team1_screenshot_url, '_blank')}
                />
              </div>
            )}
            {team1_submission_data?.team2_screenshot_url && (
              <div>
                <p className="text-sm text-gray-400 mb-2">Team 2 Screenshot:</p>
                <img
                  src={team1_submission_data.team2_screenshot_url}
                  alt="Team 2 evidence"
                  className="w-full h-40 object-cover rounded-lg border border-white/10 cursor-pointer hover:scale-105 transition-transform"
                  onClick={() => window.open(team1_submission_data.team2_screenshot_url, '_blank')}
                />
              </div>
            )}
            {!team1_submission_data?.team1_screenshot_url && !team1_submission_data?.team2_screenshot_url && (
              <p className="text-sm text-gray-400 text-center py-8">No evidence provided</p>
            )}
          </div>
        </div>

        {/* Team 2 Evidence */}
        <div className="space-y-3">
          <h4 className="font-medium text-white">{team2_name} Evidence</h4>
          <div className="space-y-2">
            {team2_submission_data?.team1_screenshot_url && (
              <div>
                <p className="text-sm text-gray-400 mb-2">Team 1 Screenshot:</p>
                <img
                  src={team2_submission_data.team1_screenshot_url}
                  alt="Team 1 evidence"
                  className="w-full h-40 object-cover rounded-lg border border-white/10 cursor-pointer hover:scale-105 transition-transform"
                  onClick={() => window.open(team2_submission_data.team1_screenshot_url, '_blank')}
                />
              </div>
            )}
            {team2_submission_data?.team2_screenshot_url && (
              <div>
                <p className="text-sm text-gray-400 mb-2">Team 2 Screenshot:</p>
                <img
                  src={team2_submission_data.team2_screenshot_url}
                  alt="Team 2 evidence"
                  className="w-full h-40 object-cover rounded-lg border border-white/10 cursor-pointer hover:scale-105 transition-transform"
                  onClick={() => window.open(team2_submission_data.team2_screenshot_url, '_blank')}
                />
              </div>
            )}
            {!team2_submission_data?.team1_screenshot_url && !team2_submission_data?.team2_screenshot_url && (
              <p className="text-sm text-gray-400 text-center py-8">No evidence provided</p>
            )}
          </div>
        </div>
      </div>

      {/* Evidence Analysis */}
      {analysis?.evidence && (
        <div className="p-4 bg-[#22262f] rounded-lg">
          <h4 className="font-medium text-white mb-2">Evidence Analysis</h4>
          <div className="text-sm text-gray-400">
            {analysis.evidence.evidenceMatches ? (
              <p className="text-green-300">✓ Both teams provided matching evidence</p>
            ) : analysis.evidence.summary.bothHaveEvidence ? (
              <p className="text-amber-300">⚠ Different evidence provided by each team</p>
            ) : analysis.evidence.summary.neitherHasEvidence ? (
              <p className="text-gray-400">– No evidence provided by either team</p>
            ) : (
              <p className="text-[#23F8B5]">
                ℹ Only {analysis.evidence.summary.onlyTeam1HasEvidence ? team1_name : team2_name} provided evidence
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return <OverviewTab />;
      case 'stats':
        return <StatsTab />;
      case 'lineup':
        return <LineupTab />;
      case 'evidence':
        return <EvidenceTab />;
      default:
        return <OverviewTab />;
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      <div className={`relative bg-[#1e2129] border border-white/10 rounded-2xl shadow-2xl overflow-hidden transition-all duration-300 ${
        isExpanded
          ? 'w-full h-full max-w-none max-h-none'
          : 'max-w-4xl w-full h-full max-h-[88vh]'
      } flex flex-col`}>

        {/* Header - Fixed */}
        <div className="flex-shrink-0 p-4 border-b border-white/[0.06] bg-[#1e2129]">
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <h2 className="text-lg font-bold text-white mb-0.5">
                Match Result Review
              </h2>
              <p className="text-xs text-gray-400 truncate">
                {tournament_name} • {team1_name} vs {team2_name}
              </p>
            </div>
            <div className="flex items-center space-x-1 flex-shrink-0">
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="p-1.5 text-gray-400 hover:text-white hover:bg-white/[0.06] rounded-lg transition-colors"
                title={isExpanded ? 'Minimize' : 'Maximize'}
              >
                {isExpanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
              </button>
              <button
                onClick={onClose}
                className="p-1.5 text-gray-400 hover:text-white hover:bg-white/[0.06] rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="mt-3 flex gap-1 bg-[#22262f] p-1 rounded-lg">
            {tabs.map((tab) => (
              <TabButton
                key={tab.id}
                tab={tab}
                isActive={activeTab === tab.id}
                onClick={() => setActiveTab(tab.id)}
              />
            ))}
          </div>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="p-4">
            {renderTabContent()}
          </div>
        </div>

        {/* Enhanced Action Panel - Fixed */}
        <div className="flex-shrink-0">
          <EnhancedActionPanel
            analysis={analysis}
            submission={submission}
            onApprove={onApprove}
            onModify={onModify}
            isLoading={isLoading}
            teamNames={{ team1: team1_name, team2: team2_name }}
          />
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SubmissionComparisonModal;